import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import MobileLayout from '@shared/components/organisms/mobile/MobileLayout';
import { useSignLineListQuery, useSignLineInfoQuery } from '@features/signLine/hooks';
import { useEmployeeInfosQuery } from '@features/employee/hooks';
import { mapSignLineMembers } from '@features/signLine/utils/mapSignLineMembers';
import chevronUpIcon from '@shared/assets/images/chevron-up_icon.svg';
import chevronDownIcon from '@shared/assets/images/chevron_down_icon.svg';

import styles from './MobileApprovalLinePage.module.scss';

/**
 * 모바일 자주 쓰는 결재라인 페이지
 * - 저장된 결재라인 목록을 조회하고 상세 정보를 표시
 * - 아코디언 형태로 결재라인 펼침/접힘 기능 제공
 */
function MobileApprovalLinePage() {
  const [expandedLineIds, setExpandedLineIds] = useState(new Set());

  // 결재라인 목록 조회
  const { data: signLineListData } = useSignLineListQuery();

  // 결재라인 목록 가공
  const signLineList = useMemo(
    function () {
      const list = signLineListData?.resultData?.signLineList || [];
      return list.map((item) => ({
        id: item.idx,
        title: item.title,
      }));
    },
    [signLineListData],
  );

  // 아코디언 토글 함수
  function handleToggleLine(lineId) {
    setExpandedLineIds(function (prev) {
      const newSet = new Set(prev);
      if (newSet.has(lineId)) {
        newSet.delete(lineId);
      } else {
        newSet.add(lineId);
      }
      return newSet;
    });
  }

  return (
    <MobileLayout title="자주 쓰는 결재라인">
      {/* 콘텐츠 영역 */}
      <div className={styles.content}>
        {signLineList.length === 0 ? (
          <div className={styles.emptyState}>등록된 결재라인이 없습니다.</div>
        ) : (
          <div className={styles.lineList}>
            {signLineList.map(function (line) {
              const isExpanded = expandedLineIds.has(line.id);

              return (
                <ApprovalLineItem
                  key={line.id}
                  lineId={line.id}
                  title={line.title}
                  isExpanded={isExpanded}
                  onToggle={handleToggleLine}
                />
              );
            })}
          </div>
        )}
      </div>
    </MobileLayout>
  );
}

/**
 * 결재라인 아이템 컴포넌트
 * - 아코디언 형태로 결재라인 상세 정보 표시
 */
function ApprovalLineItem({ lineId, title, isExpanded, onToggle }) {
  // 결재라인 상세 조회
  const { data: signLineInfoData } = useSignLineInfoQuery(lineId, {
    enabled: isExpanded, // 펼쳐졌을 때만 조회
  });

  const [members, setMembers] = useState([]);

  // 결재라인 상세 정보를 멤버 배열로 변환
  useEffect(
    function () {
      if (!signLineInfoData?.resultData || !isExpanded) {
        setMembers([]);
        return;
      }

      const mappedMembers = mapSignLineMembers(signLineInfoData.resultData);
      setMembers(mappedMembers);
    },
    [signLineInfoData, isExpanded],
  );

  // 사원 상세정보 병렬 조회
  const employeeQueries = useEmployeeInfosQuery(members);

  // 병합된 멤버 정보 계산
  const enrichedMembers = useMemo(
    function () {
      if (!members.length) return [];

      return members.map(function (member, idx) {
        const data = employeeQueries[idx]?.data?.resultData;

        return {
          ...member,
          name: data?.name ?? '-',
          team: data?.team ?? '-',
          position: data?.position ?? '',
        };
      });
    },
    [members, employeeQueries],
  );

  function handleClick() {
    onToggle(lineId);
  }

  return (
    <div className={styles.lineItem}>
      {/* 결재라인 제목 및 토글 버튼 */}
      <button type="button" className={styles.lineHeader} onClick={handleClick}>
        <span className={styles.lineTitle}>{title}</span>
        <img
          src={isExpanded ? chevronUpIcon : chevronDownIcon}
          alt={isExpanded ? '접기' : '펼치기'}
          className={styles.chevronIcon}
        />
      </button>

      {/* 펼쳐진 결재라인 상세 정보 */}
      {isExpanded && (
        <div className={styles.lineDetail}>
          <div className={styles.tableContainer}>
            {enrichedMembers.length > 0 ? (
              enrichedMembers.map(function (member, idx) {
                return (
                  <div key={member.enumber || idx} className={styles.tableRow}>
                    <div className={styles.cellNumber}>{idx + 1}</div>
                    <div className={styles.cellType}>{member.role}</div>
                    <div className={styles.cellName}>{member.name}</div>
                    <div className={styles.cellTeam}>{member.team}</div>
                  </div>
                );
              })
            ) : (
              <div className={styles.emptyDetail}>구성원이 없습니다.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

ApprovalLineItem.propTypes = {
  lineId: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default MobileApprovalLinePage;
